import { Injectable } from '@angular/core';
import { AddressRef } from '@res/ng-repository';
import { BaseSchema } from '@res/ngg-core';
import { Typesaurus, schema } from 'typesaurus';

const db = schema(($) => ({
  transactions: $.collection<Transaction>(),
  partners: $.collection<Partner>(),
  users: $.collection<User>(),
  registry: $.collection<Registry>(),
  invoices: $.collection<Invoice>(),
  issues: $.collection<Issue>(),
  servicing: $.collection<Service>(),
  operators: $.collection<Operator>(),
  notifications: $.collection<Notification>(),
}));

@Injectable({
  providedIn: 'root',
})
export class OrmService {
  public transactionCollection = db.transactions;
  public parnerCollection = db.partners;
  public usersCollection = db.users;
  public registryCollection = db.registry;
  public invoiceCollection = db.invoices;
  public issueCollection = db.issues;
  public servicesCollection = db.servicing;
  public operatorsCollection = db.operators;
  public notificationsCollection = db.notifications;
}

export type Schema = Typesaurus.Schema<typeof db>;

export type NotificationSchema = Schema['notifications'];
export type NotificationId = Schema['notifications']['Id'];
export type NotificationRef = Schema['notifications']['Ref'];
export type NotificationDoc = Schema['notifications']['Doc'];
export type NotificationData = Schema['notifications']['Data'];
export type NotificationUpdateData = Schema['notifications']['AssignData'];
export type NotificationDef = Schema['notifications']['Def'];
export type NotificationModel = Schema['notifications']['Def']['Model'];
export type NotificationResult = Schema['notifications']['Result'];
// The addressee. firestore.rules keys the partner-facing read and delete off this field, so a notification
// stored without it is reachable by admins only.
export interface Notification extends BaseSchema {
  name: string;
  userId: UserRef;
}

export type OperatorSchema = Schema['operators'];
export type OperatorId = Schema['operators']['Id'];
export type OperatorRef = Schema['operators']['Ref'];
export type OperatorDoc = Schema['operators']['Doc'];
export type OperatorData = Schema['operators']['Data'];
export type OperatorUpdateData = Schema['operators']['AssignData'];
export type OperatorDef = Schema['operators']['Def'];
export type OperatorModel = Schema['operators']['Def']['Model'];
export type OperatorResult = Schema['operators']['Result'];
// The partner this operator record belongs to. firestore.rules keys the partner-facing read off this field,
// so an operator stored without it is reachable by admins only.
export interface Operator extends BaseSchema {
  name: string;
  userId: UserRef;
}

export type ServicingSchema = Schema['servicing'];
export type ServicingId = Schema['servicing']['Id'];
export type ServicingRef = Schema['servicing']['Ref'];
export type ServicingDoc = Schema['servicing']['Doc'];
export type ServicingData = Schema['servicing']['Data'];
export type ServicingUpdateData = Schema['servicing']['AssignData'];
export type ServicingDef = Schema['servicing']['Def'];
export type ServicingModel = Schema['servicing']['Def']['Model'];
export type ServicingResult = Schema['servicing']['Result'];
export interface Service extends BaseSchema {
  name: string;
}

export type UserSchema = Schema['users'];
export type UserId = Schema['users']['Id'];
export type UserRef = Schema['users']['Ref'];
export type UserDoc = Schema['users']['Doc'];
export type UserData = Schema['users']['Data'];
export type UserUpdateData = Schema['users']['AssignData'];
export type UserDef = Schema['users']['Def'];
export type UserModel = Schema['users']['Def']['Model'];
export type UserResult = Schema['users']['Result'];
export interface User extends BaseSchema {
  name: string;
  email: string;
}

export type TransactionSchema = Schema['transactions'];
export type TransactionId = Schema['transactions']['Id'];
export type TransactionRef = Schema['transactions']['Ref'];
export type TransactionDoc = Schema['transactions']['Doc'];
export type TransactionData = Schema['transactions']['Data'];
export type TransactionUpdateData = Schema['transactions']['AssignData'];
export type TransactionDef = Schema['transactions']['Def'];
export type TransactionModel = Schema['transactions']['Def']['Model'];
export type TransactionResult = Schema['transactions']['Result'];
export interface Transaction extends BaseSchema {
  cId: string;
  userId: UserRef;
  addressId: AddressRef;
  partialPayment: boolean;
  paymentResult: string;
  tip: number;
  paymentDetails: {
    paidAmount: number;
    paymentName: string;
  };
}

export type PartnerId = Schema['partners']['Id'];
export type PartnerRef = Schema['partners']['Ref'];
export type PartnerDoc = Schema['partners']['Doc'];
export type PartnerData = Schema['partners']['Data'];
export type PartnerUpdateData = Schema['partners']['AssignData'];
export type PartnerDef = Schema['partners']['Def'];
export type PartnerModel = Schema['partners']['Def']['Model'];
export type PartnerResult = Schema['partners']['Result'];
export interface Partner extends BaseSchema {
  isIndividual: boolean;
  name: string;
  address: string;
  taxNumber: string;
  companyRegisterNumber: string;
  mothersName: string;
  birthAddress: string;
  birthDay: Date;
  drivingLicenseData: {
    number: string;
    category: string;
    validTo: Date;
  };
  telephoneNum: string;
  email: string;
}

export type RegistryId = Schema['registry']['Id'];
export type RegistryRef = Schema['registry']['Ref'];
export type RegistryDoc = Schema['registry']['Doc'];
export type RegistryData = Schema['registry']['Data'];
export type RegistryDef = Schema['registry']['Def'];
export type RegistryModel = Schema['registry']['Def']['Model'];
export type RegistryResult = Schema['registry']['Result'];
export type RegistryUpdateData = Schema['registry']['AssignData'];
export interface Registry extends BaseSchema {
  addressId: AddressRef;
  userId: UserRef;
  creationDate: Date;
  crmSignKey: string;
  deviceType: string;
  endOfGuarantee: Date;
  lastService: Date;
  appId: string;
  nextService: Date;
  type: string;
  status: string;
  cId?: string;
}

export type IssueId = Schema['issues']['Id'];
export type IssueRef = Schema['issues']['Ref'];
export type IssueDoc = Schema['issues']['Doc'];
export type IssueData = Schema['issues']['Data'];
export type IssueDef = Schema['issues']['Def'];
export type IssueModel = Schema['issues']['Def']['Model'];
export type IssueResult = Schema['issues']['Result'];
export type IssueUpdateData = Schema['issues']['AssignData'];
export interface Issue extends BaseSchema {
  userId: UserRef;
  status: string;
  subject: string;
  openOn: Date;
  category: string;
}

export type InvoiceSchema = Schema['invoices'];
export type InvoiceId = Schema['invoices']['Id'];
export type InvoiceRef = Schema['invoices']['Ref'];
export type InvoiceDoc = Schema['invoices']['Doc'];
export type InvoiceData = Schema['invoices']['Data'];
export type InvoiceUpdateData = Schema['invoices']['AssignData'];
export type InvoiceDef = Schema['invoices']['Def'];
export type InvoiceModel = Schema['invoices']['Def']['Model'];
export type InvoiceResult = Schema['invoices']['Result'];
export interface Invoice extends BaseSchema {
  amount: number;
  userId: UserRef;
  status: string;
  invoiceID: string;
}

export type InviteNewPartnerData = PartnerData & UserData & { password: string };
